import React, { memo, useContext, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import { updateLastActivity } from '../../Utils/LastActivity';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

type VesselSortingDialogProps = {
    controller: boolean,
    onClose: (success: boolean, newActiveSort: string, newSortDirection: 'asc' | 'desc') => void,
    initialActiveSort: string,
    initialSortDirection: 'asc' | 'desc',
};

function VesselSortingDialog(props: Readonly<VesselSortingDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const currentTheme: Theme = useTheme();

    const [activeSort, setActiveSort] = useState<string>('orderNo');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {
    
        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        setActiveSort(props.initialActiveSort);
        setSortDirection(props.initialSortDirection);
    }, [props.initialActiveSort, props.initialSortDirection, rerenderFlag]);

    function handleClose(success: boolean): void {
        
        updateLastActivity(userContext, localStorageContext);

        props.onClose(success, activeSort, sortDirection);

        setActiveSort('');
        setSortDirection('asc');
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => handleClose(false)}
            scroll='paper'
            maxWidth='xl'
            PaperProps={{
                sx: {
                    position: 'fixed',
                    top: 10,
                },
            }}
            TransitionProps={{
                onEntered: (): void => setRerenderFlag(new Date()),
            }}
        >
            <DialogTitle>Sort</DialogTitle>
            <DialogContent>
                <table style={{
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }}>
                    <tbody>
                        <tr>
                            <td>
                                <span style={{
                                    color: currentTheme.palette.text.primary,
                                }}>
                                    Ascending
                                </span>
                            </td>
                            <td>
                                <Switch
                                    checked={sortDirection === 'desc'}
                                    onChange={(e: React.ChangeEvent): void => setSortDirection((e.target as HTMLInputElement).checked ? 'desc' : 'asc')}
                                    color='primary'
                                />  
                            </td>
                            <td>
                                <span style={{
                                    color: currentTheme.palette.text.primary,
                                }}>
                                    Descending
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <br />
                <FormControl 
                    size='small'
                    style={{
                        width: Math.min(windowSize[0] * 0.8, 500),
                    }}
                >
                    <InputLabel>Sort By</InputLabel>
                    <Select
                        value={activeSort}
                        onChange={(e: SelectChangeEvent): void => setActiveSort((e.target as HTMLInputElement).value)}
                        size='small'
                        variant='outlined'
                        label='Sort By'
                    >
                        <MenuItem
                            value='orderNo'
                        >
                            Order NO
                        </MenuItem>
                        <MenuItem
                            value='vesselName'
                        >
                            Vessel Name
                        </MenuItem>
                        <MenuItem
                            value='shipOrderedDate'
                        >
                            Ship Ordered Date
                        </MenuItem>
                        <MenuItem
                            value='subject'
                        >
                            Subject
                        </MenuItem>
                        <MenuItem
                            value='department'
                        >
                            Department
                        </MenuItem>
                        <MenuItem
                            value='orderType'
                        >
                            Order Type
                        </MenuItem>
                        <MenuItem
                            value='priority'
                        >
                            Priority
                        </MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={(): void => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        minWidth: '100px',
                        maxWidth: '100px',
                        minHeight: '32px',
                        maxHeight: '32px',
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={(): void => handleClose(true)}
                    color='primary'
                    variant='contained'
                    style={{
                        minWidth: '100px',
                        maxWidth: '100px',
                        minHeight: '32px',
                        maxHeight: '32px',
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default memo(VesselSortingDialog);
